// src/features/analytics/components/AnalyticsSubaccountsPage.tsx

import { useState } from 'react';
import { Group, Paper, SegmentedControl, Select, Skeleton, Stack, Table } from '@mantine/core';
import {
  ANALYTICS_METRICS,
  ANALYTICS_METRIC_LABELS,
  SUBACCOUNTS_DEFAULT_LIMIT,
  SUBACCOUNTS_MAX_LIMIT,
  type AnalyticsMetric,
} from '@/lib/constants/analytics';
import { AnalyticsApi } from '../AnalyticsApi';
import { AnalyticsUtils } from '../AnalyticsUtils';
import { useAnalyticsRange } from '../useAnalyticsRange';
import { useAnalyticsScope } from '../useAnalyticsScope';
import { useAnalyticsSubaccounts } from '../useAnalyticsSubaccounts';
import { CsvDownloadButton } from '@/shared/components/CsvDownloadButton';
import { AnalyticsCoverageNotice } from './AnalyticsCoverageNotice';
import { AnalyticsErrorState } from './AnalyticsErrorState';

const LIMIT_OPTIONS = [String(SUBACCOUNTS_DEFAULT_LIMIT), String(SUBACCOUNTS_MAX_LIMIT)];

/** Network scope only (the layout bounces account scope back to the overview). */
export function AnalyticsSubaccountsPage() {
  const { search, range } = useAnalyticsRange();
  const { scope } = useAnalyticsScope(search.scope);
  const networkCid = scope?.kind === 'network' ? scope.cid : null;

  const [metric, setMetric] = useState<AnalyticsMetric>('watchTime');
  const [limit, setLimit] = useState(SUBACCOUNTS_DEFAULT_LIMIT);

  const { subaccounts, isLoading, isError, error, refetch } = useAnalyticsSubaccounts(
    networkCid,
    range,
    metric,
    limit
  );

  if (networkCid === null) return null;

  const rows = subaccounts?.data.rows ?? [];

  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="wrap">
        <Group gap="sm">
          <SegmentedControl
            size="xs"
            data={ANALYTICS_METRICS.map((m) => ({ value: m, label: ANALYTICS_METRIC_LABELS[m] }))}
            value={metric}
            onChange={(value) => setMetric(value as AnalyticsMetric)}
          />
          <Select
            size="xs"
            w={90}
            data={LIMIT_OPTIONS}
            value={String(limit)}
            onChange={(value) => value && setLimit(Number(value))}
            allowDeselect={false}
            aria-label="Row limit"
          />
        </Group>
        <CsvDownloadButton
          download={() => AnalyticsApi.downloadSubaccountsCsv(networkCid, range, metric, limit)}
          fallbackFilename="analytics-network-subaccounts.csv"
        />
      </Group>

      {isError ? (
        <AnalyticsErrorState error={error} onRetry={refetch} />
      ) : isLoading ? (
        <Skeleton height={240} />
      ) : rows.length === 0 ? (
        <AnalyticsCoverageNotice
          coverage={subaccounts?.coverage}
          freshness={subaccounts?.freshness}
          range={range}
          emptyMessage="No playback analytics for this range. Playback analytics begins with the new player rollout."
        />
      ) : (
        <Paper withBorder>
          <AnalyticsCoverageNotice
            coverage={subaccounts?.coverage}
            freshness={subaccounts?.freshness}
            range={range}
          />
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={50}>#</Table.Th>
                <Table.Th>Account</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Sessions</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Watch time</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Unique viewers</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row, index) => (
                <Table.Tr key={row.cid}>
                  <Table.Td>{index + 1}</Table.Td>
                  <Table.Td>{row.label}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    {AnalyticsUtils.formatCount(row.sessions)}
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    {AnalyticsUtils.formatWatchTime(row.watchTimeMs)}
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    {AnalyticsUtils.formatCount(row.uniqueViewers)}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}
